import { combineReducers, createReducer, isAnyOf } from '@reduxjs/toolkit';

import { EXPIRED_ERROR } from 'constants/Global';

import { createToggleReducer } from 'modules/utils';
import { answerWorksSuccess } from 'modules/entity/subEntities/intervention/actions/works';
import { fetchPaymentsSuccess } from 'modules/entity/subEntities/intervention/actions/payment';
import {
  INTERVENTION_UPDATE_FROM_SOCKET,
  MESSAGE_RECEIVED_FROM_SOCKET,
  REACTION_RECEIVED_FROM_SOCKET,
  REACTION_REMOVED_FROM_SOCKET,
} from 'modules/websockets/actionTypes';
import {
  acceptRentalDocumentsSuccess,
  acceptRepairOrderSuccess,
  confirmClientDocumentsSuccess,
  confirmDropOffSuccess,
  confirmPickUpSuccess,
  openDropOffLockerSuccess,
  openPickUpLockerSuccess,
  reportIssuesSuccess,
} from 'modules/entity/subEntities/appointment/actions';
import {
  callMeBackSuccess,
  sendContactFormSuccess,
  sendOpinionSuccess,
} from 'modules/entity/subEntities/intervention/actions/general';

import { formatEntity } from '../utils';
import {
  closeSignatureModal,
  closeSignHereModal,
  fetchEntity,
  fetchEntityFailure,
  fetchEntitySuccess,
  markSignatureDocumentsDismissed,
  openSignatureModal,
  openSignHereModal,
  sendRating,
  sendRatingFailure,
  sendRatingSuccess,
  setDocumentSignatureLoading,
} from '../actions';

/**
 * @typedef {{
 *  data: import('../types').Entity,
 *  ui: {
 *    loading: boolean,
 *    documentSignatureLoading: boolean,
 *    isSignatureModalOpen: boolean,
 *    isSignHereModalOpen: boolean,
 *    dismissedSignatureDocumentIds: Array<string>,
 *    ratingIsLoading: boolean,
 *  },
 * }} EntityState
 */

/**
 * @param {EntityState} _
 * @param {import('@reduxjs/toolkit').PayloadAction<import('../types').Entity>} action
 * @returns {EntityState}
 */
const setFromEntity = (state, { payload }) => formatEntity(state, payload);

/**
 * @param {import('../types').Entity} state
 * @param {import('modules/websockets/types').MessageReceivedAction} action
 * @returns {import('../types').Entity}
 */
const setFromMessagesSocket = (state, { payload: data }) => ({
  ...state,
  readMessagesUrl: data.readMessagesUrl,
});

/**
 * @param {import('../types').Entity} state
 * @param {import('modules/entity/subEntities/intervention/types').FetchPaymentsSuccessAction} action
 * @returns {import('../types').Entity}
 */
const setFromPayments = (state, { payload: payments }) => ({
  ...state,
  payments,
});

export const isSignatureModalOpen = createToggleReducer(openSignatureModal, closeSignatureModal, INTERVENTION_UPDATE_FROM_SOCKET);

export const isSignHereModalOpen = createToggleReducer(openSignHereModal, closeSignHereModal, INTERVENTION_UPDATE_FROM_SOCKET);

export const dismissedSignatureDocumentIds = createReducer([], (builder) => {
  builder.addCase(markSignatureDocumentsDismissed, (_, { payload }) => payload);
});

export const ratingIsLoading = createToggleReducer(sendRating, sendRatingSuccess, sendRatingFailure);

export default combineReducers({
  data: createReducer(null, (builder) => {
    builder
      .addCase(fetchEntitySuccess, (_, { payload }) => payload)
      .addCase(fetchEntityFailure, (state, { payload: error }) => {
        const { status, data = {} } = error.response ?? {};
        if (status === EXPIRED_ERROR) {
          return { ...data.data, errorStatus: data.errorStatus };
        }
        return state;
      })
      .addCase(fetchPaymentsSuccess.type, setFromPayments)
      .addCase(MESSAGE_RECEIVED_FROM_SOCKET, setFromMessagesSocket)
      .addCase(REACTION_REMOVED_FROM_SOCKET, setFromMessagesSocket)
      .addCase(REACTION_RECEIVED_FROM_SOCKET, setFromMessagesSocket)
      .addMatcher(
        isAnyOf(
          sendRatingSuccess.match,
          answerWorksSuccess.match,
          callMeBackSuccess.match,
          sendOpinionSuccess.match,
          reportIssuesSuccess.match,
          sendContactFormSuccess.match,
          acceptRepairOrderSuccess.match,
          confirmClientDocumentsSuccess.match,
          acceptRentalDocumentsSuccess.match,
          confirmDropOffSuccess.match,
          confirmPickUpSuccess.match,
          openDropOffLockerSuccess.match,
          openPickUpLockerSuccess.match
        ),
        setFromEntity
      );
  }),
  ui: combineReducers({
    loading: createReducer(false, (builder) => {
      builder
        .addCase(fetchEntity, (state, { meta }) => (meta.refresh ? state : true))
        .addMatcher(isAnyOf(fetchEntitySuccess.match, fetchEntityFailure.match), () => false);
    }),
    documentSignatureLoading: createReducer(false, (builder) => {
      builder.addCase(setDocumentSignatureLoading, (_, { payload }) => payload);
    }),
    isSignatureModalOpen,
    isSignHereModalOpen,
    dismissedSignatureDocumentIds,
    ratingIsLoading,
  }),
});
